import React, { useState, useEffect, useCallback } from "react";
import cn from "classnames";
import { useNavigate } from "react-router-dom";
import styles from "./Overview.module.sass";
import TooltipGlodal from "../../../components/TooltipGlodal";
import Card from "../../../components/Card";
import Icon from "../../../components/Icon";
import Tooltip from "../../../components/Tooltip";
import ModalAddCompany from "../../../components/ModalAddCompany";
import { getCurrentStudentInfo } from "../../../services/account.service";
import { getUnreadNotificationsCount } from "../../../services/notification.service";
import { getFoldersByStudentId } from "../../../services/folder.service";
import { getStudentForCurrentUser, getStudentById } from "../../../services/student.service";
import { updateFolder } from "../../../services/folder.service";
import { showError, showSuccess } from "../../../services/toast.service";

import {pb} from "../../../services/pocketbase";

const getContractStatus = (folders) => {
  if (!folders || folders.length === 0) {
    return {
      counter: "Aucun contrat",
      statusColor: "red",
      statusText: "Aucun contrat",
      tooltip: "Aucun contrat d'alternance n'a été trouvé. Contactez votre école pour plus d'informations.",
      route: null
    };
  }

  // Trier les dossiers par date de création décroissante et prendre le plus récent
  const sortedFolders = [...folders].sort((a, b) => new Date(b.created) - new Date(a.created));
  const folder = sortedFolders[0];
  
  const studentInfoComplete = folder?.progression?.etudiant?.infos_remplies;
  const hasCompany = !!folder?.expand?.company_id?.name;
  const cerfaGenerated = folder?.progression?.ecole?.cerfa_genere;
  const studentSigned = folder?.progression?.etudiant?.sign;
  const companySigned = folder?.progression?.entreprise?.sign;
  const schoolSigned = folder?.progression?.ecole?.sign;

  // Vérifier si toutes les signatures sont complètes
  const allSigned = studentSigned && companySigned && schoolSigned;

  // Vérifier si l'étudiant attend que l'école génère les documents
  const waitingForSchool = studentInfoComplete && hasCompany && !cerfaGenerated;

  // Si toutes les signatures sont complètes
  if (allSigned) {
    return {
      counter: "Signé",
      statusColor: "green",
      statusText: "Validé",
      tooltip: "Votre contrat d'alternance a été signé et validé par toutes les parties. Vous pouvez le consulter à tout moment.",
      route: `/student/folder/${folder.id}/validate`
    };
  }

  // Si le CERFA est généré et que l'étudiant n'a pas encore signé
  if (cerfaGenerated && !studentSigned) {
    return {
      counter: "À signer",
      statusColor: "purple",
      statusText: "À signer",
      tooltip: "Votre contrat est prêt à être signé. Cliquez pour accéder à la signature.",
      route: `/student/folder/${folder.id}/validate`
    };
  }

  // Si l'étudiant attend que l'école génère les documents
  if (waitingForSchool) {
    return {
      counter: "En attente",
      statusColor: "purple",
      statusText: "En attente",
      tooltip: "Votre dossier est complet. L'école prépare les documents de signature.",
      route: null
    };
  }

  // Si les informations étudiant ne sont pas complètes (priorité 1)
  if (!studentInfoComplete) {
    return {
      counter: "À compléter",
      statusColor: "red",
      statusText: "À compléter",
      tooltip: "Vous devez compléter les informations de votre contrat d'alternance.",
      route: `/student/folder/${folder.id}/edit`
    };
  }

  // Si aucune entreprise n'est associée (priorité 2)
  if (!hasCompany) {
    return {
      counter: "Entreprise manquante",
      statusColor: "red",
      statusText: "Entreprise manquante",
      tooltip: "Vous devez associer une entreprise à votre contrat d'alternance. Cliquez pour ajouter une entreprise.",
      route: "add-company", // Route spéciale pour ouvrir la modal
      folderId: folder.id
    };
  }

  // Cas par défaut
  return {
    counter: "En cours",
    statusColor: "purple",
    statusText: "En cours",
    tooltip: "Votre contrat est en cours de traitement.",
    route: null
  };
};

const getBaseItems = (studentData, unreadNotificationsCount, folders) => {
  const isFileComplete = studentData?.file_status === 'Complet';
  
  const hasUnreadNotifications = unreadNotificationsCount > 0;
  const contractStatus = getContractStatus(folders);
  
  return [
    {
      title: "Mes informations",
      counter: isFileComplete ? "Complet" : "Incomplet",
      icon: "profile-circle",
      background: "#edf8f2",
      statusColor: isFileComplete ? "green" : "red",
      statusText: isFileComplete ? "Complet" : "Incomplet",
      tooltip: isFileComplete 
        ? "Votre dossier est complet. Toutes les informations nécessaires ont été fournies."
        : "Suivi de vos documents d'alternance. Il vous reste des documents à fournir pour compléter votre dossier.",
      route: "/student/account"
    },
    {
      title: "Mon contrat",
      counter: contractStatus.counter,
      icon: "edit",
      background: "#ecf9fe",
      statusColor: contractStatus.statusColor,
      statusText: contractStatus.statusText,
      tooltip: contractStatus.tooltip,
      route: contractStatus.route,
      folderId: contractStatus.folderId // 🎯 Nécessaire pour l'ajout d'entreprise
    },
    {
      title: "Notifications",
      counter: unreadNotificationsCount > 0 ? unreadNotificationsCount.toString() : "0",
      icon: "notification",
      background: "#f2efff",
      statusColor: hasUnreadNotifications ? "purple" : "green",
      statusText: hasUnreadNotifications ? "À lire" : "Aucune nouvelle",
      tooltip: hasUnreadNotifications 
        ? `Vous avez ${unreadNotificationsCount} nouvelle${unreadNotificationsCount > 1 ? 's' : ''} notification${unreadNotificationsCount > 1 ? 's' : ''} concernant votre parcours d'alternance et vos échéances.`
        : "Aucune nouvelle notification. Vous êtes à jour !",
      route: "/student/notifications"
    },
  ];
};

const Overview = ({ className, currentUser }) => {
  const navigate = useNavigate();
  const [studentData, setStudentData] = useState(null);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleModalAddCompany, setVisibleModalAddCompany] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [isUpdatingFolder, setIsUpdatingFolder] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      if (!currentUser?.student_id) {
        return;
      }

      const student = await getStudentById(currentUser.student_id);
      if (student) {
        setStudentData(student);
        const studentFolders = await getFoldersByStudentId(student.id);
        setFolders(studentFolders);
      }

      const notificationsCount = await getUnreadNotificationsCount();
      setUnreadNotificationsCount(notificationsCount);

    } catch (error) {
      console.error("Erreur lors de la récupération des données:", error);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.student_id]);

  useEffect(() => {
    fetchData();
    
    const setupRealtimeListeners = () => {
      if (!currentUser?.student_id) {
        return;
      }

      pb.collection('students').subscribe(currentUser.student_id, (e) => {
        fetchData();
      });

      pb.collection('folders').subscribe('*', (e) => {
        if (e.record?.student_id === currentUser.student_id) {
          fetchData();
        }
      });

      pb.collection('notifications').subscribe('*', (e) => {
        if (e.record?.user_id === currentUser.student_id) {
          fetchData();
        }
      });
    };

    setupRealtimeListeners();

    return () => {
      pb.collection('students').unsubscribe();
      pb.collection('folders').unsubscribe();
      pb.collection('notifications').unsubscribe();
    };
  }, [fetchData]);

  const handleItemClick = (item) => {
    if (item.route === "add-company") {
      setSelectedFolderId(item.folderId);
      setVisibleModalAddCompany(true);
    } else if (item.route) {
      navigate(item.route);
    }
  };

  const handleCompanySave = useCallback(
    async (company) => {
      if (!company?.id || !selectedFolderId) {
        showError("Erreur : données manquantes pour associer l'entreprise au dossier");
        return;
      }

      setIsUpdatingFolder(true);
      try {
        await updateFolder(selectedFolderId, {
          company_id: company.id,
        });

        showSuccess(`Entreprise "${company.name}" associée au dossier avec succès`);
        
        const student = await getStudentForCurrentUser();
        if (student?.id) {
          const studentFolders = await getFoldersByStudentId(student.id);
          setFolders(studentFolders);
        }
        
        setVisibleModalAddCompany(false);
        setSelectedFolderId(null);
      } catch (error) {
        showError("Erreur lors de l'association de l'entreprise au dossier");
      } finally {
        setIsUpdatingFolder(false);
      }
    },
    [selectedFolderId]
  );

  const items = getBaseItems(studentData, unreadNotificationsCount, folders);

  return (
    <>
      <Card
        className={cn(styles.card, className)}
        title={`Votre tableau de bord, ${currentUser?.first_name || 'Utilisateur'}`}
        classTitle="title-yellow"
      >
        <div className={styles.overview}>
          <div className={styles.list}>
            {loading ? (
              <div>Chargement...</div>
            ) : (
              items.map((x, index) => (
              <div
                className={cn(styles.item, { [styles.clickable]: x.route })}
                key={index}
                style={{ backgroundColor: x.background }}
                onClick={() => handleItemClick(x)}
              >
                <div className={styles.icon}>
                  <Icon name={x.icon} size="24" />{" "}
                </div>
                <div className={styles.line}>
                  <div className={styles.details}>
                    <div className={styles.category}>
                      {x.title}
                      <Tooltip
                        className={styles.tooltip}
                        title={x.tooltip}
                        icon="info"
                        place="right"
                      />
                    </div>
                    <div className={styles.counter}>{x.counter}</div>
                    <div className={styles.indicator}>
                      {x.statusText && (
                        <div
                          className={cn(
                            { "status-purple": x.statusColor === "purple" },
                            { "status-green-dark": x.statusColor === "green" },
                            { "status-red-dark": x.statusColor === "red" },
                            styles.status
                          )}
                        >
                          {x.statusText}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              ))
            )}
          </div>
        </div>
      </Card>
      <ModalAddCompany
        visible={visibleModalAddCompany}
        onClose={() => {
          setVisibleModalAddCompany(false);
          setSelectedFolderId(null);
        }}
        onSave={handleCompanySave}
      />
      <TooltipGlodal />
    </>
  );
};

export default Overview;
